export interface CircleMember {
  id: string;
  human: { id: string; name: string; pictureUrl: string | null };
  status: string;
  joinedAt: string | null;
}

export interface PlannedGathering {
  title: string;
  description: string;
  format: string;
  cadence: string | null;
}

export interface CandidateTeam {
  circleId: string;
  name: string;
  existingMemberCount: number;
}

/** Three-surface circle model. */
export type CircleType = 'circle' | 'coalition' | 'league';

export interface CoalitionCampaign {
  id: string;
  title: string;
  description: string;
  displayOrder: number;
}

export interface Circle {
  id: string;
  name: string;
  displayTitle: string | null;
  description: string | null;
  coverUrl: string | null;
  category: string;
  access: string;
  type: CircleType;
  brandName: string | null;
  alumniTeamsEnabled: boolean;
  candidateTeams: CandidateTeam[];
  memberCount: number;
  myStatus: string | null;
  memberAvatars?: string[];
  plannedGatherings?: PlannedGathering[];
  members: CircleMember[];
  createdAt: string;
  // Coalition-only fields.
  problemHook: string | null;
  problemStatement: string | null;
  campaigns: CoalitionCampaign[];
}

export interface HumanCircle {
  id: string;
  circle: Circle;
  status: string;
  confidence: number | null;
  joinedAt: string | null;
}

export interface CirclePostReaction {
  id: string;
  user: { id: string; name: string };
  reaction: string;
}

export interface CirclePostComment {
  id: string;
  author: { id: string; name: string; pictureUrl: string | null };
  content: string;
  createdAt: string;
}

export interface ExploreCard {
  id: string;
  title: string;
  text: string;
  human: { id: string; name: string; pictureUrl: string | null };
}

/**
 * Human-readable label for a circle category ("Interest Circle", "Alumni Circle", etc.).
 * Falls back to title-casing the raw category string for unknown values.
 */
/**
 * Display name for a circle. Names are stored with their brand prefix
 * baked in ("Leaders.org University of Cambridge Alumni", "Quintessential
 * Single Malt Connoisseurs"), so the UI shows the name as-is. Kept as a
 * function so callers can stay agnostic if the display convention shifts.
 */
export function circleDisplayName(name: string | null | undefined): string {
  return (name ?? '').trim();
}

export function circleCategoryLabel(category: string): string {
  switch (category) {
    case 'organization': return 'Alumni Circle';
    case 'role':         return 'Role Circle';
    case 'industry':     return 'Industry Circle';
    case 'interest':     return 'Interest Circle';
    case 'cause':        return 'Cause Circle';
    case 'context':      return 'Context Circle';
    case 'goal':         return 'Goal Circle';
    default:
      return `${category.charAt(0).toUpperCase()}${category.slice(1)} Circle`;
  }
}

export interface CirclePost {
  id: string;
  circleId: string;
  author: { id: string; name: string; pictureUrl: string | null };
  content: string;
  linkUrl: string | null;
  linkTitle: string | null;
  linkDescription: string | null;
  linkImageUrl: string | null;
  linkDomain: string | null;
  attachments: { id: string; originalName: string; url: string; fileSize: number; mimeType: string }[];
  comments: CirclePostComment[];
  reactions: CirclePostReaction[];
  createdAt: string;
  updatedAt: string;
}
